// @ts-nocheck
import { createStyle } from '@gluestack-style/react';

/**
 * Icon Component Theme Configuration
 *
 * Size variants pass `props.size` to the underlying Lucide SVG icon AND set
 * container dimensions (h/w) using theme tokens.
 *
 * ## The "Great Icon Distortion" Fix (Jan 2026)
 * Fixing icons required balancing two opposing layout behaviors:
 * 1. **Expansion**: Without `h`/`w` constraints, Icons in flex containers (like the Admin Search bar)
 *    would expand to fill all available space, becoming massive.
 * 2. **Distortion/Clipping**: With strict `h`/`w` constraints, SVG paths were being clipped or
 *    squashed if the container was even a fraction of a pixel off.
 *
 * ## The Solution
 * - **NO explicit h/w**: Forced dimensions cause the browser to SCALE the SVG, which shifts
 *   individual path positions (the "displaced block" bug).
 * - **maxHeight/maxWidth**: Prevents expansion in flex containers WITHOUT forcing scaling.
 *   The SVG sizes itself via props.size; the container just caps the maximum.
 * - **flexShrink/Grow: 0**: Prevents flex layout from squashing/stretching the container.
 * - **overflow: visible**: Allows any sub-pixel overflow to render without clipping.
 *
 * ## Size variants (SVG intrinsic size, capped by maxHeight/maxWidth)
 * - 3xs: 10px, 2xs: 12px, xs: 14px, sm: 16px, md: 18px, lg: 20px, xl: 24px, 2xl: 28px
 */
export const BaseIconVariants = {
  size: {
    '3xs': {
      maxHeight: 10,
      maxWidth: 10,
      props: {
        size: 10,
      },
    },
    '2xs': {
      maxHeight: 12,
      maxWidth: 12,
      props: {
        size: 12,
      },
    },
    xs: {
      maxHeight: 14,
      maxWidth: 14,
      props: {
        size: 14,
      },
    },
    sm: {
      maxHeight: 16,
      maxWidth: 16,
      props: {
        size: 16,
      },
    },
    md: {
      maxHeight: 18,
      maxWidth: 18,
      props: {
        size: 18,
      },
    },
    lg: {
      maxHeight: 20,
      maxWidth: 20,
      props: {
        size: 20,
      },
    },
    xl: {
      maxHeight: 24,
      maxWidth: 24,
      props: {
        size: 24,
      },
    },
    '2xl': {
      maxHeight: 28,
      maxWidth: 28,
      props: {
        size: 28,
      },
    },
  },
};

export const BaseIcon = createStyle({
  color: '$backgroundLight800',
  _dark: {
    color: '$backgroundDark400',
  },
  props: {
    fill: 'none',
    stroke: 'currentColor',
  },
  variants: BaseIconVariants,
  // Prevent expansion/shrinking in flex contexts
  flexShrink: 0,
  flexGrow: 0,
  // Allow SVG to render outside bounds if needed
  overflow: 'visible',
});

export const Icon = createStyle({
  variants: BaseIconVariants,
  props: {
    size: 'md',
    fill: 'none',
    stroke: 'currentColor',
  },
  color: '$backgroundLight800',
  _dark: {
    color: '$backgroundDark400',
  },
  // Prevent expansion/shrinking in flex contexts
  flexShrink: 0,
  flexGrow: 0,
  // Allow SVG to render outside bounds if needed
  overflow: 'visible',
});
